/**
 * Read selected entries out of a .zip WITHOUT loading the whole archive.
 *
 * Google Takeout splits a Business Profile export into ~2 GB zips that are
 * almost entirely photos and video; the reviews are a few MB of JSON. JSZip
 * (and any whole-file approach) needs the archive in one ArrayBuffer, which
 * Chrome caps at 2^31 − 1 bytes — the first real export (2,149,425,832 bytes)
 * failed exactly there. This reads only the central directory from the end of
 * the file and then just the wanted entries, via Blob.slice(), so memory stays
 * at the size of the JSON regardless of the media.
 *
 * Supports stored (0) and deflate (8) entries, and zip64 archives/entries.
 * Not supported (not produced by Takeout): encryption, multi-disk archives.
 */

export interface ZipTextEntry {
  name: string;
  text: string;
}

const EOCD = 0x06054b50;
const ZIP64_LOCATOR = 0x07064b50;
const ZIP64_EOCD = 0x06064b50;
const CENTRAL = 0x02014b50;
const LOCAL = 0x04034b50;
const MAX_U32 = 0xffffffff;

async function bytes(blob: Blob, start: number, end: number): Promise<DataView> {
  return new DataView(await blob.slice(start, end).arrayBuffer());
}

function u64(view: DataView, at: number): number {
  // Archive offsets fit comfortably in 2^53; anything larger is not a real file.
  return view.getUint32(at, true) + view.getUint32(at + 4, true) * 0x100000000;
}

async function inflateRaw(data: Blob): Promise<string> {
  // DecompressionStream('deflate-raw'): Chrome 103+, Safari 16.4+, Node 21.2+.
  const stream = (data as any).stream().pipeThrough(new (globalThis as any).DecompressionStream('deflate-raw'));
  return new Response(stream).text();
}

/** Every entry whose name satisfies `wanted`, decoded as UTF-8 text. */
export async function readZipEntries(file: Blob, wanted: (name: string) => boolean): Promise<ZipTextEntry[]> {
  // End of central directory: last 22 bytes + an optional comment of up to 64 KiB.
  const tailStart = Math.max(0, file.size - 22 - 0xffff);
  const tail = await bytes(file, tailStart, file.size);
  let eocd = -1;
  for (let i = tail.byteLength - 22; i >= 0; i--) {
    if (tail.getUint32(i, true) === EOCD) {
      eocd = i;
      break;
    }
  }
  if (eocd < 0) throw new Error('Not a zip file (no end-of-central-directory record).');

  let entries = tail.getUint16(eocd + 10, true);
  let cdSize = tail.getUint32(eocd + 12, true);
  let cdOffset = tail.getUint32(eocd + 16, true);

  if (entries === 0xffff || cdSize === MAX_U32 || cdOffset === MAX_U32) {
    const loc = eocd - 20;
    if (loc < 0 || tail.getUint32(loc, true) !== ZIP64_LOCATOR) {
      throw new Error('zip64 archive without a zip64 locator.');
    }
    const z64At = u64(tail, loc + 8);
    const z64 = await bytes(file, z64At, z64At + 56);
    if (z64.getUint32(0, true) !== ZIP64_EOCD) throw new Error('Bad zip64 end-of-central-directory record.');
    entries = u64(z64, 32);
    cdSize = u64(z64, 40);
    cdOffset = u64(z64, 48);
  }

  const cd = await bytes(file, cdOffset, cdOffset + cdSize);
  const utf8 = new TextDecoder('utf-8');
  const found: { name: string; method: number; compSize: number; localOffset: number }[] = [];

  let p = 0;
  for (let n = 0; n < entries; n++) {
    if (cd.getUint32(p, true) !== CENTRAL) throw new Error(`Bad central directory entry at ${cdOffset + p}.`);
    const method = cd.getUint16(p + 10, true);
    let compSize = cd.getUint32(p + 20, true);
    let uncompSize = cd.getUint32(p + 24, true);
    const nameLen = cd.getUint16(p + 28, true);
    const extraLen = cd.getUint16(p + 30, true);
    const commentLen = cd.getUint16(p + 32, true);
    let localOffset = cd.getUint32(p + 42, true);
    const name = utf8.decode(new Uint8Array(cd.buffer, cd.byteOffset + p + 46, nameLen));

    // zip64 extra field (0x0001): the 8-byte values for whichever fields were
    // saturated, in the fixed order uncompressed, compressed, offset.
    if (uncompSize === MAX_U32 || compSize === MAX_U32 || localOffset === MAX_U32) {
      let e = p + 46 + nameLen;
      const end = e + extraLen;
      while (e + 4 <= end) {
        const id = cd.getUint16(e, true);
        const size = cd.getUint16(e + 2, true);
        if (id === 0x0001) {
          let q = e + 4;
          if (uncompSize === MAX_U32) (uncompSize = u64(cd, q)), (q += 8);
          if (compSize === MAX_U32) (compSize = u64(cd, q)), (q += 8);
          if (localOffset === MAX_U32) localOffset = u64(cd, q);
          break;
        }
        e += 4 + size;
      }
    }

    if (!name.endsWith('/') && wanted(name)) {
      if (method !== 0 && method !== 8) throw new Error(`${name}: unsupported compression method ${method}.`);
      found.push({ name, method, compSize, localOffset });
    }
    p += 46 + nameLen + extraLen + commentLen;
  }

  const out: ZipTextEntry[] = [];
  for (const f of found) {
    // The local header repeats name/extra with its OWN lengths — read them
    // rather than assuming the central directory's.
    const local = await bytes(file, f.localOffset, f.localOffset + 30);
    if (local.getUint32(0, true) !== LOCAL) throw new Error(`${f.name}: bad local header.`);
    const dataStart = f.localOffset + 30 + local.getUint16(26, true) + local.getUint16(28, true);
    const data = file.slice(dataStart, dataStart + f.compSize);
    const text = f.method === 0 ? await data.text() : await inflateRaw(data);
    out.push({ name: f.name, text });
  }
  return out;
}
